import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { GeneralTaskApi, GeneralTaskDTO } from './GeneralTaskApi';
import { TaskStatusOptions, TASK_PRIORITIES } from '../../constants/taskConstants';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { GENERAL_TASK_CATEGORIES } from '../../constants/taskConstants';
import GeneralTaskHelp from './GeneralTaskHelp';

export type FormMode = 'create' | 'edit' | 'view';

interface Props {
  mode: FormMode;
}

export const GeneralTaskForm: React.FC<Props> = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const taskState = location.state;
  const [isHelpOpen, setHelpOpen] = useState(false);

  const {
    parentId = '',
    title: parentTitle = '',
    description: parentDescription = '',
    status: parentStatus = '',
    levelNumber
  } = (location.state || {}) as {
    parentId?: string;
    title?: string;
    description?: string;
    status?: string;
    levelNumber?: string;
  };

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const tenantId = user?.tenantId || '';
  const tenantName = user?.tenantName || '';
  const userId = user?.id || '';
  const entityType = taskState?.entityType || '';
  const entityId = taskState?.entityId || '';
  const taskType = taskState?.taskType || 'GENERAL_TASK';
  // Initialize task with default values
  const [task, setTask] = useState<GeneralTaskDTO>({
    id: '',
    tenantId,
    tenantName,
    title: '',
    description: '',
    category: 'GENERAL_TASK',
    parentTaskId: parentId,
    taskLevel: Number(levelNumber) || 1,
    status: 'DRAFT',
    entityType, 
    entityId,
    taskType,
    priority: 'MEDIUM',
    isBillable: false,
    billed: false,
    estimatedHours: 0,
    actualHours: 0,
    startTime: new Date().toISOString().slice(0, 16),
    endTime: '',
    dueDate: '',
    assignedToId: '',
    assignedToName: '',
    createdBy: userId,
    updatedBy: userId,
    isActive: true,
    isDeleted: false,
    createdAt: '',
    updatedAt: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const isReadOnly = mode === 'view';

  useEffect(() => {
    if ((mode === 'edit' || mode === 'view') && id) {
      fetchTask(id);
    }
  }, [id, mode]);

  const fetchTask = async (taskId: string) => {
    setLoading(true);
    try {
      const res = await GeneralTaskApi.getById(taskId);
      setTask(res.data);
    } catch (err) {
      setError('Failed to load task.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;

    if ((name === 'estimatedHours' || name === 'actualHours') && Number(value) < 0) return;

    setTask((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (task.endTime && task.startTime && new Date(task.endTime) < new Date(task.startTime)) {
      alert('End time cannot be earlier than start time.');
      return;
    }

    try {
      if (mode === 'create') {
        await GeneralTaskApi.create(task);
        alert('Task created successfully!');
      } else if (mode === 'edit' && id) {
        await GeneralTaskApi.update(id, task);
        alert('Task updated successfully!');
      }
      navigate(-1);
    } catch (err) {
      alert('Failed to save task.');
    }
  };

  const renderInput = (
    label: string,
    name: keyof GeneralTaskDTO,
    type: string = 'text'
  ) => (
    <div>
      <label className="block text-sm font-semibold mb-1">{label}</label>
      <input
        type={type}
        name={name}
        value={(task[name] as any) || ''}
        onChange={handleChange}
        readOnly={isReadOnly}
        className={`border px-3 py-2 rounded w-full ${isReadOnly ? 'bg-gray-100' : ''}`}
      />
    </div>
  );

  const renderTextArea = (label: string, name: keyof GeneralTaskDTO) => (
    <div className="col-span-full">
      <label className="block text-sm font-semibold mb-1">{label}</label>
      <textarea
        name={name}
        value={(task[name] as any) || ''}
        onChange={handleChange}
        readOnly={isReadOnly}
        className={`border px-3 py-2 rounded w-full ${isReadOnly ? 'bg-gray-100' : ''}`}
        rows={3}
      />
    </div>
  );

  const renderTitle = (label: string, name: keyof GeneralTaskDTO, type: string = 'text') => (
    <div className="col-span-full">
      <label className="block text-sm font-semibold mb-1">{label}</label>
      <input
        type={type}
        name={name}
        value={(task[name] as any) || ''}
        onChange={handleChange}
        readOnly={isReadOnly}
        className={`border px-3 py-2 rounded w-full ${isReadOnly ? 'bg-gray-100' : ''}`}
      />
    </div>
  );

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6 max-w-4xl mx-auto bg-white shadow rounded">
      {error && <div className="mb-4 text-red-600">{error}</div>}
      <div className="relative">
        <button
          className="absolute right-0 top-0 text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
          onClick={() => setHelpOpen(true)}
        >
          <FontAwesomeIcon icon={faCircleInfo} />
          Help
        </button>
      </div>

      {parentId && (
        <div className="mb-6 p-4 bg-gray-100 rounded">
          <h3 className="font-bold text-lg mb-2">Parent Task</h3>
          <div><strong>Title:</strong> {parentTitle}</div>
          <div><strong>Description:</strong> {parentDescription}</div>
          <div><strong>Level:</strong> {levelNumber}</div>
          <div><strong>Status:</strong> {parentStatus}</div>
        </div>
      )}
    {entityType && (
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium">Entity Type</label>
          <input
            type="text"
            value={taskState?.entityType}
            disabled
            className="w-full p-2 border border-gray-300 rounded bg-gray-100"
          />
        </div>

        <div>
          <label className="block text-sm font-medium">Entity ID</label>
          <input
            type="text"
            value={taskState?.entityId}
            disabled
            className="w-full p-2 border border-gray-300 rounded bg-gray-100"
          />
        </div>

        <div>
          <label className="block text-sm font-medium">Task Type</label>
          <input
            type="text"
            value={taskState?.taskType}
            disabled
            className="w-full p-2 border border-gray-300 rounded bg-gray-100"
          />
        </div>
      </div>
    )}

      {taskState && taskState?.metadata && (
        <div className="bg-gray-50 border border-gray-200 p-4 rounded">
          <h3 className="font-semibold mb-2">Contract Details</h3>
          <ul className="space-y-1 text-sm">
            {Object.entries(taskState.metadata).map(([key, value]) => (
              <li key={key}>
                <span className="font-medium capitalize">{key.replace(/([A-Z])/g, ' $1')}:</span>{' '}
                <span className="text-gray-700">{String(value) || ''}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {renderTitle('Title', 'title')}
        {renderTextArea('Description', 'description')}

        <div>
          <label className="block text-sm font-semibold mb-1">Category</label>
          <select
            name="category"
            value={task.category || 'DATA_ENTRY'}
            onChange={handleChange}
            disabled={isReadOnly}
            className={`border px-3 py-2 rounded w-full ${isReadOnly ? 'bg-gray-100' : ''}`}
          >
            {GENERAL_TASK_CATEGORIES.map((cat) => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
        </div>

        {renderInput('Estimated Hours', 'estimatedHours', 'number')}
        {renderInput('Actual Hours', 'actualHours', 'number')}
        {renderInput('Start Time', 'startTime', 'datetime-local')}
        {renderInput('End Time', 'endTime', 'datetime-local')}
        {renderInput('Due Date', 'dueDate', 'date')}

        <div>
          <label className="block text-sm font-semibold mb-1">Priority</label>
          <select
            name="priority"
            value={task.priority || ''}
            onChange={handleChange}
            disabled={isReadOnly}
            className={`border px-3 py-2 rounded w-full ${isReadOnly ? 'bg-gray-100' : ''}`}
          >
            <option value="">Select</option>
            {TASK_PRIORITIES.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">Status</label>
          <select
            name="status"
            value={task.status || ''}
            onChange={handleChange}
            disabled={isReadOnly}
            className={`border px-3 py-2 rounded w-full ${isReadOnly ? 'bg-gray-100' : ''}`}
          >
            {TaskStatusOptions.map((status) => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>
        </div>

        <div className="col-span-full flex gap-4 mt-6">
          {mode !== 'view' && (
            <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded">
              {mode === 'create' ? 'Create' : 'Update'}
            </button>
          )}
          <button type="button" onClick={() => navigate(-1)} className="bg-gray-600 text-white px-4 py-2 rounded">
            Back
          </button>
        </div>
      </form>
      <GeneralTaskHelp isOpen={isHelpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
};